import React from "react";
import { CCField } from "@pymblecitizencoders/cc-ui";
import { EventDoc } from "../../types/event";
import "./eventManager.css";

/** Which end of the event this field belongs to. */
type Which = "in" | "out";

interface EventBumpTimeProps {
	which: Which;
	event: EventDoc;
	readOnly: boolean;
	onChange: (patch: Partial<EventDoc>) => void;
}

const FIELD: Record<
	Which,
	{ key: "BumpIn" | "BumpOut"; id: string; label: string }
> = {
	in: { key: "BumpIn", id: "em-bump-in", label: "Bump in time" },
	out: { key: "BumpOut", id: "em-bump-out", label: "Bump out time" },
};

/**
 * When the crew may start setting up, or when everything must be out.
 *
 * ONE FIELD, rendered twice — bump in and bump out, side by side in one row of
 * the sidebar, under the event's own start and end.
 *
 * A TIME AND NO DATE, stored exactly as typed. `HH:MM` is both what
 * `<input type="time">` emits and what the API keeps — a bump time is a call
 * time read against the day the event runs, so there is no instant to build and
 * nothing to convert. The API refuses a date-carrying value rather than
 * truncating it, which is what this component used to send.
 *
 * NO CROSS-FIELD ERROR, and losing the date is what took it away. This once
 * flagged a bump in later than the start and a bump out earlier than the end.
 * A bare time cannot say which day it means: `01:00` against a concert ending
 * at 22:00 is an overnight pack-down, the commonest shape there is, and the
 * warning fired on precisely that case. The API makes the same call and
 * validates the format alone — see `_bump_time_fields` in
 * `event_admin_helpers.py`. The calendar reads each time as its nearest
 * occurrence outside the event, which is the same reading in pixels.
 */
export const EventBumpTime: React.FC<EventBumpTimeProps> = ({
	which,
	event,
	readOnly,
	onChange,
}) => {
	const { key, id, label } = FIELD[which];
	const value = event[key] ?? "";

	/**
	 * Clearing the field clears the stored value outright: `null`, not an empty
	 * string, so the save says "remove this" rather than writing something the
	 * API has to interpret. (It takes `""` to mean the same thing, but only
	 * because an emptied time input sends it — saying so plainly is better than
	 * relying on that.)
	 */
	const set = (time: string): void => {
		onChange({ [key]: time || null });
	};

	return (
		<CCField
			id={id}
			label={label}
			type="time"
			fullWidth
			/* `em-time-field` for the spacing the sidebar already gives the start
			   and end time fields, so this sits under its neighbour on the same
			   rhythm. `--empty` hides the browser's own "--:--" while there is
			   nothing in the field — see the CSS; without it the dashes sit under
			   the label, which rests inside the field until there is a value, and
			   the two read as one struck-through word. */
			className={`em-time-field${value ? "" : " em-time-field--empty"}`}
			value={value}
			disabled={readOnly}
			onChange={(e) => set(e.target.value)}
		/>
	);
};
